/**
 * @fileoverview Restore-tenant mutation hook.
 * @description Wraps POST /sales/tenants/:id/restore in a TanStack mutation, invalidating every
 * tenant query (list and detail forms alike) on success.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { TENANTS_QUERY_KEY } from '@/features/sales/hooks/useTenants'
import { restoreTenant } from '@/features/sales/services/tenants.service'

/**
 * @description Restores a previously archived tenant with a mandatory reason note and refreshes
 * tenant queries.
 * @returns {ReturnType<typeof useMutation<Awaited<ReturnType<typeof restoreTenant>>, unknown, {
 * id: string; reason: string }>>} The mutation object (mutate with `{ id, reason }`, isPending,
 * ...).
 */
export function useRestoreTenant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => restoreTenant(id, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TENANTS_QUERY_KEY })
    },
  })
}
